import React, { useState } from 'react';
import { Modal, Button, Space } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;

  .anticon {
    font-size: 48px;
  }
`;

const Title = styled.h3`
  text-align: center;
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
`;

const Description = styled.p`
  text-align: center;
  color: #666;
  margin: 0;
  font-size: 14px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
`;

export default function ConfirmModal({
    open,
    onConfirm,
    onCancel,
    title = 'Confirmar ação',
    description = 'Tem certeza que deseja continuar?',
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    type = 'warning',
    icon,
    loading = false,
    confirmButtonProps = {},
    cancelButtonProps = {},
    children
}) {
    const typeConfig = {
        warning: {
            color: '#faad14',
            icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />
        },
        danger: {
            color: '#ff4d4f',
            icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
        },
        info: {
            color: '#1890ff',
            icon: <ExclamationCircleOutlined style={{ color: '#1890ff' }} />
        },
        success: {
            color: '#52c41a',
            icon: <ExclamationCircleOutlined style={{ color: '#52c41a' }} />
        }
    };

    const config = typeConfig[type] || typeConfig.warning;
    const displayIcon = icon || config.icon;

    const isDanger = type === 'danger';

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            centered
            width={400}
            closable={false}
        >
            <IconWrapper>
                {displayIcon}
            </IconWrapper>

            <Title>{title}</Title>
            <Description>{description}</Description>

            {children}

            <ButtonsWrapper>
                <Button
                    onClick={onCancel}
                    disabled={loading}
                    {...cancelButtonProps}
                >
                    {cancelText}
                </Button>
                <Button
                    type="primary"
                    danger={isDanger}
                    onClick={onConfirm}
                    loading={loading}
                    {...confirmButtonProps}
                >
                    {confirmText}
                </Button>
            </ButtonsWrapper>
        </Modal>
    );
}

// Hook para uso imperativo
export function useConfirmModal() {
    const [modalState, setModalState] = useState({
        open: false,
        loading: false,
        config: {}
    });

    const showConfirm = (config = {}) => {
        return new Promise((resolve) => {
            setModalState({
                open: true,
                loading: false,
                config: {
                    ...config,
                    onConfirm: async () => {
                        if (config.onConfirm) {
                            setModalState(prev => ({ ...prev, loading: true }));
                            try {
                                await config.onConfirm();
                            } finally {
                                setModalState(prev => ({ ...prev, loading: false }));
                            }
                        }
                        setModalState(prev => ({ ...prev, open: false }));
                        resolve(true);
                    },
                    onCancel: () => {
                        setModalState(prev => ({ ...prev, open: false }));
                        config.onCancel?.();
                        resolve(false);
                    }
                }
            });
        });
    };

    const closeModal = () => {
        setModalState(prev => ({ ...prev, open: false }));
    };

    const ConfirmModalComponent = () => (
        <ConfirmModal
            open={modalState.open}
            loading={modalState.loading}
            {...modalState.config}
        />
    );

    return {
        showConfirm,
        closeModal,
        ConfirmModal: ConfirmModalComponent
    };
}